/**
 * Small display helpers both clients need. Formatting only — no business rules.
 *
 * Deliberately depends on nothing: the address helpers take a structural shape
 * rather than importing the wire schema, so this module (and the rest of the
 * domain core) stays free of zod and can be typechecked and tested on its own.
 */
export function formatAddress(p) {
    if (!p)
        return '';
    const street = [p.line1, p.line2].filter(Boolean).join(', ');
    return [street, p.city, [p.state, p.postalCode].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ');
}
export function formatAddressShort(p) {
    if (!p)
        return '';
    return [p.line1, p.city].filter(Boolean).join(', ');
}
/** Digits only, for `tel:` and `sms:` links and for phone search. */
export function normalizePhone(phone) {
    return (phone ?? '').replace(/\D/g, '');
}
/**
 * Every way the same US number might already be stored.
 *
 * `normalizePhone` strips punctuation and nothing else, so the same person
 * saved twice can sit in the database under two different keys:
 *
 *   "(310) 502-1690"   -> 3105021690
 *   "+1 310 502 1690"  -> 13105021690
 *
 * Comparing those as strings says they are different people. They are not,
 * and a duplicate check built on `normalizePhone` alone would wave the second
 * one through — which is the whole thing it exists to stop.
 *
 * So this returns the forms worth looking for, for use with an `in` query. It
 * returns digit strings rather than one canonical value deliberately: the
 * stored column is populated by `normalizePhone` and indexed, and matching a
 * small set against an index is cheaper and far less invasive than a migration
 * to re-key every existing row.
 *
 * Non-US numbers come back as the single form they were given. Guessing a
 * country code for a number that does not look North American would invent
 * matches between people who share nothing but a suffix.
 */
export function phoneMatchDigits(phone) {
    const digits = normalizePhone(phone);
    if (!digits)
        return [];
    const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    // Only widen for something that actually looks like a US number.
    if (national.length !== 10)
        return [digits];
    return [...new Set([digits, national, `1${national}`])];
}
export function formatPhone(phone) {
    const d = normalizePhone(phone);
    if (d.length === 10)
        return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    if (d.length === 11 && d.startsWith('1'))
        return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
    return phone ?? '';
}
/** Apple Maps on iOS, Google Maps elsewhere. */
export function mapsUrl(p, platform = 'ios') {
    const q = encodeURIComponent(formatAddress(p));
    return platform === 'ios'
        ? `http://maps.apple.com/?daddr=${q}&dirflg=d`
        : `https://www.google.com/maps/dir/?api=1&destination=${q}`;
}
export function initials(name) {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join('');
}
/**
 * The units "3 hours ago" is built from, largest first.
 *
 * `one` is the word English uses instead of "1 <unit> ago" — "yesterday",
 * not "1 day ago". Hours and minutes have no such word, so they count.
 */
const RELATIVE_UNITS = [
    { unit: 'year', ms: 31_536_000_000, past: 'last year', future: 'next year' },
    { unit: 'month', ms: 2_592_000_000, past: 'last month', future: 'next month' },
    { unit: 'day', ms: 86_400_000, past: 'yesterday', future: 'tomorrow' },
    { unit: 'hour', ms: 3_600_000 },
    { unit: 'minute', ms: 60_000 },
];
/**
 * "20 minutes ago", "yesterday", "in 3 hours".
 *
 * Written out by hand rather than with `Intl.RelativeTimeFormat`, which is
 * the one piece of `Intl` that Hermes does not implement. On a phone it is
 * `undefined`, so constructing it threw, and on the new React Native
 * architecture that took the whole app down at launch rather than showing an
 * error. `Intl.DateTimeFormat` and `Intl.NumberFormat` are fine there and are
 * still used elsewhere in this file.
 *
 * The wording follows what `RelativeTimeFormat` produced with
 * `numeric: 'auto'`, so nothing reads differently on the web.
 */
export function relativeTime(value, now = new Date()) {
    const d = typeof value === 'string' ? new Date(value) : value;
    const at = d.getTime();
    if (!Number.isFinite(at))
        return '';
    const diff = at - now.getTime();
    const abs = Math.abs(diff);
    const past = diff < 0;
    for (const { unit, ms, past: pastWord, future } of RELATIVE_UNITS) {
        if (abs < ms)
            continue;
        const n = Math.round(abs / ms);
        if (n === 1 && pastWord && future)
            return past ? pastWord : future;
        const noun = n === 1 ? unit : `${unit}s`;
        return past ? `${n} ${noun} ago` : `in ${n} ${noun}`;
    }
    return 'just now';
}
export function formatDate(value, locale = 'en-US') {
    const d = typeof value === 'string' ? new Date(value) : value;
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
}
export function formatDateTime(value, locale = 'en-US') {
    const d = typeof value === 'string' ? new Date(value) : value;
    return new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(d);
}
export function formatTime(value, locale = 'en-US') {
    const d = typeof value === 'string' ? new Date(value) : value;
    return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(d);
}
/** `2026-09-09` in LOCAL time — never `toISOString().slice(0,10)`, which is UTC. */
export function toLocalDateKey(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
export function isSameLocalDay(a, b) {
    return toLocalDateKey(a) === toLocalDateKey(b);
}
//# sourceMappingURL=format.js.map